import styled from '@emotion/styled';
import { captureException } from '@sentry/minimal';
import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, RouteComponentProps } from 'react-router-dom';
import { appClient } from 'src/config/apollo';
import { config } from 'src/config/config';
import { toastNotificationsService } from 'src/config/rootService';
import {
    ContentCollectionLinkLinkType,
    ScheduledContentActivityType,
    ScheduledContentType,
} from 'src/generated/webapp_gql';
import { Button } from 'src/shared/components/Button';

import { color, font, layout, mediaQueries } from 'src/shared/styles/variables';
import { useGetScheduledContentQuery } from '../documents/getScheduledContent.generated';
import { useUpdateScheduledContentActivityMutation } from '../documents/updateScheduledContentActivity.generated';

import moment from 'moment';
import { Markdown } from 'src/modules/newPrograms/components/Markdown';
import { formatDailyPauseMoment } from '../util/dateTime';

const Container = styled.div({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    flexGrow: 1,
});

const ContentContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
    height: '100%',
    margin: '0 2rem',
    [mediaQueries.xs]: {
        margin: '0.5rem 1rem',
    },
});

const LinkContainer = styled.div({
    marginTop: 36,
    marginBottom: 24,
    // This creates a "gutter" to consume the left and right
    // padding of the link buttons.
    marginLeft: -8,
    marginRight: -8,
    flexWrap: 'nowrap',
    [mediaQueries.sm]: {
        flexWrap: 'wrap',
    },
    width: '100%',
    display: 'flex',
});

const Wrapper = styled.div({
    flexGrow: 1,
    position: 'relative',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    backgroundColor: color.white,
    overflow: 'hidden',
    height: '100%',
    width: '100%',
    margin: '1rem 2rem',
    maxWidth: layout.containerWidth,
});

const Banner = styled.div<{ accentColor: string }>(({ accentColor }) => ({
    height: 180,
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    marginBottom: 14,
    borderRadius: 8,
    backgroundColor: accentColor,
    overflow: 'hidden',
}));

const ContentWrapper = styled.div({
    paddingTop: 16,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
});
const TextHeader = styled.p({
    color: color.ebonyClay,
    marginBottom: 4,
    ...font.heavy(font.size.large),
});
const TextSubtitle = styled.p({
    color: color.ebonyClay,
    ...font.light(font.size.medium),
});

const TextContentWrapper = styled.div({
    marginTop: 12,
    marginBottom: 48,
});

const MarkdownContainer = styled.div({
    '.markdown p': {
        ...font.roman(font.size.medium),
        marginBottom: 10,
    },
});

interface MatchParams {
    uuid: string;
}

type Props = RouteComponentProps<MatchParams, {}, { uuid?: string } | undefined>;

export const DailyPause: React.FC<Props> = ({ match, location }) => {
    const { t, i18n } = useTranslation();

    // Get the Pause uuid from the route parameter
    let { uuid = null } = match.params;
    // Fallback to internal state otherwise
    if (!uuid && location.state?.uuid) {
        uuid = location.state.uuid;
    }

    // TODO: Consider setting this in the Apollo cache.
    const isCompleted = useRef(false);

    const { data, loading, error, refetch } = useGetScheduledContentQuery({
        variables: { uuid, contentType: ScheduledContentType.DailyPause },
        fetchPolicy: 'cache-first',
        // When we call refetch() we don't want this component to
        // update.
        notifyOnNetworkStatusChange: false,
        client: appClient,
    });
    const refetchRef = useRef(refetch);
    refetchRef.current = refetch;

    const [updateActivity] = useUpdateScheduledContentActivityMutation({
        client: appClient,
        fetchPolicy: 'no-cache',
    });

    useEffect(() => {
        if (error) {
            toastNotificationsService.renderErrorToast(t(config.messages.genericError));
        }
    }, [error]);

    const [cta] = useMemo(() => {
        const ctaLink =
            data?.scheduledContent?.scheduledContentLinks?.find(link => link.isCta) ?? null;

        // TODO(evanmh): Implement views for non-cta links.

        return [ctaLink];
    }, [data]);

    const buildCtaButton = () => {
        if (!cta) {
            return null;
        }

        const {
            linkType,
            mobileValue: mobileLink,
            webValue: webLink,
            title = t('dailyPause:content_modal.cta_fallback'),
        } = cta;

        switch (linkType) {
            case ContentCollectionLinkLinkType.Meditation:
                return (
                    <Button fullWidth to={`/meditations?uuid=${webLink || mobileLink}`}>
                        {title}
                    </Button>
                );
            case ContentCollectionLinkLinkType.Program:
                return (
                    <Button fullWidth to={`/programs?uuid=${webLink || mobileLink}`}>
                        {title}
                    </Button>
                );
            case ContentCollectionLinkLinkType.InternalLink:
                // Don't fallback to mobile links for internal links because
                // the member web app can't handle "deep links" like
                // modernhealth://circles
                if (!webLink) {
                    captureException(
                        new Error(
                            `Daily pause attempted to render an internal link (${
                                mobileLink || 'unknown'
                            }) on web but no corresponding web link was found.`,
                        ),
                    );
                    return null;
                }

                return (
                    <Button fullWidth to={webLink}>
                        {title}
                    </Button>
                );
            case ContentCollectionLinkLinkType.ExternalLink:
                return (
                    <Button fullWidth href={webLink || mobileLink} target="_blank">
                        {title}
                    </Button>
                );
            case ContentCollectionLinkLinkType.CircleTopic:
            case ContentCollectionLinkLinkType.CircleSeries:
            case ContentCollectionLinkLinkType.CircleSession:
                captureException(new Error(`Unhandled daily pause link type ${linkType}`));
                break;
            default:
                const exhaustiveCheck: never = linkType;

                captureException(
                    new Error(`Unrecognized daily pause link type ${exhaustiveCheck}`),
                );
        }

        return null;
    };

    useEffect(() => {
        if (!isCompleted.current && !error && !loading && uuid) {
            isCompleted.current = true;

            updateActivity({
                variables: {
                    uuid,
                    activityType: ScheduledContentActivityType.Completed,
                },
            })
                .catch(err => captureException(err))
                .finally(() => {
                    // Update the scheduled content.
                    // Because we're using a cache policy this will also trigger
                    // an update on the homescreen.
                    refetchRef.current?.();
                });
        }
    }, [uuid, loading, error]);

    const accentBackground = data?.scheduledContent?.accentColor
        ? `#${data?.scheduledContent?.accentColor}`
        : color.white;

    if (!loading && (!data || !data.scheduledContent)) {
        return <Redirect to="/dashboard" />;
    }

    return (
        <Container>
            <Wrapper>
                <ContentContainer>
                    <Banner accentColor={accentBackground}>
                        {data?.scheduledContent?.headerImage && (
                            <img src={data.scheduledContent.headerImage} />
                        )}
                    </Banner>
                    {data?.scheduledContent && (
                        <div
                            style={{
                                maxWidth: layout.containerWidth / 2,
                                width: '100%',
                            }}
                        >
                            <ContentWrapper>
                                <TextHeader>{data.scheduledContent.bannerTitle}</TextHeader>
                                <TextSubtitle>
                                    {t('dailyPause:content_modal.date_subtitle', {
                                        formattedDate: formatDailyPauseMoment(
                                            moment(),
                                            i18n.language,
                                        ),
                                    })}
                                </TextSubtitle>
                                <TextContentWrapper>
                                    <MarkdownContainer>
                                        <Markdown text={data.scheduledContent.fullContent} />
                                    </MarkdownContainer>
                                </TextContentWrapper>
                            </ContentWrapper>
                            <LinkContainer>
                                {cta ? (
                                    <div
                                        style={{
                                            flex: '3 0',
                                            padding: 8,
                                        }}
                                    >
                                        {buildCtaButton()}
                                    </div>
                                ) : null}

                                <div
                                    style={{
                                        flex: '1 1',
                                        padding: 8,
                                    }}
                                >
                                    <Button
                                        type="white2"
                                        fullWidth
                                        to={{
                                            pathname: '/pause/complete',
                                            state: {
                                                uuid,
                                            },
                                        }}
                                    >
                                        {t('dailyPause:content_modal.done')}
                                    </Button>
                                </div>
                            </LinkContainer>
                        </div>
                    )}
                </ContentContainer>
            </Wrapper>
        </Container>
    );
};

export default DailyPause;
